import { useCallback } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useSceneStore } from '@/stores';
import { getRouteStep } from '@/services/location-service';
import { persistStep } from '@/api/agency-quote-api';
import { applyPersistData } from '@/stores/sync';

/**
 * Hook for syncing validation scopes with the server and applying errors to forms.
 * Similar to useFieldBinding's onBlur functionality but for multiple scopes.
 */
export function useValidationSync(validationScopes: string[], form?: UseFormReturn) {
  const syncValidationScopes = useCallback(async () => {
    const { step_id, entity_index } = getRouteStep();
    if (!step_id || entity_index === undefined) return;

    const { serverState, pendingChanges } = useSceneStore.getState();
    const scopedEntries = Object.entries(serverState).filter(([, field]) => {
      return validationScopes.includes(field.scope);
    });

    if (scopedEntries.length === 0) return;

    const scopedPayload = Object.fromEntries(
      scopedEntries.map(([key, field]) => [key, pendingChanges[key] ?? field.value])
    );

    const persistedKeys = Object.keys(scopedPayload);

    const sceneData = await persistStep(step_id, entity_index, scopedPayload);
    if (getRouteStep().step_id !== step_id) return;

    applyPersistData(sceneData.step_state, persistedKeys);

    // Apply validation errors to form if provided
    if (form) {
      const updatedFields = useSceneStore.getState().serverState;
      persistedKeys.forEach((key) => {
        const field = updatedFields[key];
        if (!field) return;

        if (field.valid === false && field.message) {
          form.setError(key, { type: 'server', message: field.message });
        } else {
          form.clearErrors(key);
        }
      });
    }
  }, [validationScopes, form]);

  return { syncValidationScopes };
}
